import { colors as themeColors } from '../utils/theme';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    TextField,
    Button,
    CircularProgress,
    Paper,
    Tooltip,
    IconButton,
    Snackbar,
    Alert,
    Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useAuth } from '../context/AuthContext';
import { getCurrentFYear } from '../utils/financialYear';
import DropdownProductCodeClientMaster from '../components/DropdownProductCodeClientMaster';

export default function PartyWiseItemDialog({ open, onClose, partyId, partyName, connectionString, inline = false, tempRows, setTempRows, isFinalSubmitting = false }) {
    // When tempRows/setTempRows are supplied (inline usage from ClientMaster),
    // "Add Product" stages rows locally instead of hitting the database — the
    // actual insert happens later from the parent's Submit/Update button, same
    // as ClientConsigneeMaster's temp-row pattern.
    const useTempRows = typeof setTempRows === 'function';
    const { user, selectedCompany } = useAuth();
    const API_BASE = `${connectionString}/api`;
    const companyId = selectedCompany?.CompId;
    const AppRegID = selectedCompany?.AppRegID;
    const GCompID = selectedCompany?.GCompId;
    const fYear = getCurrentFYear();
    const userId = user?.UserId;
    const BranchId = selectedCompany?.BranchId;

    // States
    const [gridRows, setGridRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Search & Form Fields
    const [searchItemCode, setSearchItemCode] = useState('');
    const [itemSuggestions, setItemSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [productCode, setProductCode] = useState('');
    const [saleRate, setSaleRate] = useState('');
    const [purchaseRate, setPurchaseRate] = useState('');
    const [disc, setDisc] = useState('');
    const [editingRowId, setEditingRowId] = useState(null);

    // Fetch party-wise items on dialog open
    useEffect(() => {
        if (open && partyId) {
            fetchPartyWiseItems();
        }
    }, [open, partyId]);

    const fetchPartyWiseItems = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await axios.get(`${API_BASE}/partywise/getByParty/${partyId}`, {
                params: { companyId, AppRegID },
            });
            if (Array.isArray(data)) {
                const transformedRows = data.map((item, index) => ({
                    id: item.PartyWiseRateID || index,
                    PartyWiseRateID: item.PartyWiseRateID,
                    PartyId: item.PartyId,
                    ItemID: item.ItemID,
                    SrNo: item.SrNo,
                    itemCode: item.itemCode,
                    description: item.description,
                    SaleRate: Number(item.SaleRate || 0),
                    PurchaseRate: Number(item.PurchaseRate || 0),
                    Disc: Number(item.Disc || 0),
                }));
                setGridRows(transformedRows);
            }
        } catch (error) {
            console.error('Error fetching party-wise items:', error);
            showSnackbar('Failed to load party items', 'error');
        } finally {
            setLoading(false);
        }
    }, [API_BASE, partyId, companyId, AppRegID]);

    // Search items from productmaster
    const handleSearchItem = useCallback(async (searchTerm) => {
        if (!searchTerm || searchTerm.length < 2) {
            setItemSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        try {
            const { data } = await axios.get(`${API_BASE}/partywise/searchProducts`, {
                params: { searchTerm, companyId, AppRegID },
            });
            console.log('🔍 Search Results:', data);
            if (Array.isArray(data) && data.length > 0) {
                setItemSuggestions(data);
                setShowSuggestions(true);
            } else {
                setItemSuggestions([]);
                setShowSuggestions(true);
            }
        } catch (error) {
            console.error('❌ Error searching items:', error);
            setItemSuggestions([]);
            setShowSuggestions(false);
        }
    }, [API_BASE, companyId, AppRegID]);

    const handleItemSelect = (item) => {
        setSelectedItem(item);
        setProductCode(item.itemCode);
        setSaleRate(item.saleRate.toString());
        setPurchaseRate(item.purchaseRate.toString());
        setDisc(item.SaleDisc?.toString() || '0');
        setSearchItemCode(item.itemCode);
        setShowSuggestions(false);
        setEditingRowId(null);
    };

    const handleEditRow = (row) => {
        setProductCode(row.itemCode);
        setSaleRate(row.SaleRate.toString());
        setPurchaseRate(row.PurchaseRate.toString());
        setDisc(row.Disc.toString());
        setSearchItemCode(row.itemCode);
        setEditingRowId(row.id);
        setSelectedItem({ itemCode: row.itemCode, ItemID: row.ItemID });
    };

    const handleAddProduct = () => {
        if (isFinalSubmitting) return;

        if (!selectedItem) {
            showSnackbar('Please select an item', 'warning');
            return;
        }

        if (!saleRate || !purchaseRate) {
            showSnackbar('Please enter rates', 'warning');
            return;
        }

        const newRow = {
            id: editingRowId || (useTempRows ? `temp-${Date.now()}` : Date.now()),
            isTemp: useTempRows ? true : undefined,
            PartyWiseRateID: editingRowId ? [...(tempRows || []), ...gridRows].find(r => r.id === editingRowId)?.PartyWiseRateID : null,
            PartyId: partyId,
            ItemID: selectedItem.ItemID,
            itemCode: selectedItem.itemCode,
            description: selectedItem.description,
            SaleRate: Number(saleRate),
            PurchaseRate: Number(purchaseRate),
            Disc: Number(disc || 0),
        };

        if (useTempRows) {
            // Stage locally — the actual insert/update happens later from the parent's
            // Submit/Update button (same pattern as Consignee Management). This applies
            // whether the row is brand-new OR an edit of an already-persisted DB row —
            // an edited DB row is moved out of gridRows and into tempRows (carrying its
            // real PartyWiseRateID) so the parent's submit sends it as an UPDATE, not a
            // silently-dropped no-op.
            const isEditingExistingDbRow = editingRowId && !(tempRows || []).some(r => r.id === editingRowId);
            if (isEditingExistingDbRow) {
                setGridRows(prev => prev.filter(r => r.id !== editingRowId));
                setTempRows(prev => [...prev, newRow]);
            } else if (editingRowId) {
                setTempRows(prev => prev.map(r => (r.id === editingRowId ? newRow : r)));
            } else {
                setTempRows(prev => [...prev, newRow]);
            }
        } else if (editingRowId) {
            setGridRows(prev => prev.map(r => (r.id === editingRowId ? newRow : r)));
        } else {
            setGridRows(prev => [...prev, newRow]);
        }

        setEditingRowId(null);
        resetFormFields();
        showSnackbar(editingRowId ? 'Item updated' : 'Item added successfully', 'success');
    };

    const handleDeleteRow = (rowId) => {
        // A row can live in either array — a still-unsaved temp row, or an
        // already-persisted DB row loaded into gridRows — so remove it from
        // whichever one actually has it instead of assuming based on useTempRows.
        if (useTempRows && (tempRows || []).some(r => r.id === rowId)) {
            setTempRows(prev => prev.filter(r => r.id !== rowId));
        } else {
            setGridRows(prev => prev.filter(r => r.id !== rowId));
        }
        showSnackbar('Item removed', 'success');
    };

    const handleSave = async () => {
        if (gridRows.length === 0) {
            showSnackbar('Please add at least one item', 'warning');
            return;
        }

        try {
            setSaving(true);
            const payload = {
                partyId,
                items: gridRows.map(row => ({
                    PartyWiseRateID: row.PartyWiseRateID || null,
                    ItemID: row.ItemID,
                    SaleRate: row.SaleRate,
                    PurchaseRate: row.PurchaseRate,
                    Disc: row.Disc,
                })),
                companyId,
                AppRegID,
                GCompID,
                userId,
                BranchId,
                fYear,
            };

            await axios.post(`${API_BASE}/partywise/saveRates`, payload);
            showSnackbar('Party-wise rates saved successfully', 'success');
            onClose();
            fetchPartyWiseItems();
        } catch (error) {
            console.error('Error saving party-wise rates:', error);
            showSnackbar('Failed to save party-wise rates', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteDb = async (rowId) => {
        const row = [...(tempRows || []), ...gridRows].find(r => r.id === rowId);
        if (!row?.PartyWiseRateID) {
            handleDeleteRow(rowId);
            return;
        }

        try {
            await axios.delete(`${API_BASE}/partywise/deleteRate/${row.PartyWiseRateID}`, {
                params: { companyId, AppRegID },
            });
            handleDeleteRow(rowId);
            showSnackbar('Item deleted successfully', 'success');
        } catch (error) {
            console.error('Error deleting item:', error);
            showSnackbar('Failed to delete item', 'error');
        }
    };

    const resetFormFields = () => {
        setSearchItemCode('');
        setProductCode('');
        setSaleRate('');
        setPurchaseRate('');
        setDisc('');
        setSelectedItem(null);
        setShowSuggestions(false);
    };

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleSnackbarClose = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const columns = [
        { field: 'itemCode', headerName: 'Product Code', width: 200, flex: 1 },
        {
            field: 'SaleRate',
            headerName: 'Sale Rate',
            width: 90,
            align: 'right',
            renderCell: params => Number(params.row.SaleRate).toFixed(2),
        },
        {
            field: 'PurchaseRate',
            headerName: 'Purchase Rate',
            width: 100,
            align: 'right',
            renderCell: params => Number(params.row.PurchaseRate).toFixed(2),
        },
        {
            field: 'Disc',
            headerName: 'Disc (%)',
            width: 80,
            align: 'right',
            renderCell: params => Number(params.row.Disc).toFixed(2),
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 100,
            sortable: false,
            renderCell: params => (
                <Box sx={{ display: 'flex', gap: '4px' }}>
                    <Tooltip title="Edit">
                        <IconButton
                            size="small"
                            onClick={() => handleEditRow(params.row)}
                            sx={{ color: 'primary.main' }}
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <IconButton
                            size="small"
                            onClick={() => handleDeleteDb(params.row.id)}
                            sx={{ color: 'error.main' }}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
        },
    ];

    // Shared body (search/form + datagrid) used by both the inline panel and the popup dialog.
    const body = (
        <>
            {/* Search & Form Section */}
            <Paper sx={{ p: 1.5, backgroundColor: themeColors.neutral.surfaceMuted }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {/* Item Details - 4 Fields Only (single searchable Product Code field) */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5 }}>
                        <DropdownProductCodeClientMaster
                            id="productCode"
                            connectionString={connectionString}
                            companyId={companyId}
                            AppRegID={AppRegID}
                            value={productCode}
                            onSelect={handleItemSelect}
                            label="Product Code"
                            searchBy="code"
                        />
                        <TextField
                            label="Sale Rate"
                            type="number"
                            value={saleRate}
                            onChange={e => setSaleRate(e.target.value)}
                            size="small"
                            inputProps={{ step: '0.01' }}
                        />
                        <TextField
                            label="Purchase Rate"
                            type="number"
                            value={purchaseRate}
                            onChange={e => setPurchaseRate(e.target.value)}
                            size="small"
                            inputProps={{ step: '0.01' }}
                        />
                        <TextField
                            label="Discount (%)"
                            type="number"
                            value={disc}
                            onChange={e => setDisc(e.target.value)}
                            size="small"
                            inputProps={{ step: '0.01' }}
                        />
                    </Box>

                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleAddProduct}
                        fullWidth
                        size="small"
                    >
                        {editingRowId ? 'Update Product' : 'Add Product'}
                    </Button>
                </Box>
            </Paper>

            {/* DataGrid */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Paper sx={{ height: 280 }}>
                    <DataGrid
                        rows={useTempRows ? [...tempRows, ...gridRows] : gridRows}
                        columns={columns}
                        pageSizeOptions={[5, 10, 25]}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 5 } },
                        }}
                        density="compact"
                        sx={{ border: 'none', fontSize: '0.8rem' }}
                    />
                </Paper>
            )}
        </>
    );

    const snackbarEl = (
        <Snackbar
            open={snackbar.open}
            autoHideDuration={3000}
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
            <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
                {snackbar.message}
            </Alert>
        </Snackbar>
    );

    // Inline mode: render as a plain embedded panel (like Consignee Management),
    // no popup Dialog wrapper.
    if (inline) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography fontWeight={600} sx={{ fontSize: '0.95rem' }}>
                    Partywise Item Rate - {partyName}
                </Typography>

                {body}

                {/* When staging into tempRows, there's nothing to separately save here —
                    rows are committed to the database from the parent's own Submit/Update
                    button (see ClientMaster's submitAllPartyWiseItems), same as Consignee. */}
                {!useTempRows && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button
                            onClick={handleSave}
                            variant="contained"
                            size="small"
                            disabled={gridRows.length === 0 || saving}
                        >
                            {saving ? <CircularProgress size={20} /> : 'Save'}
                        </Button>
                    </Box>
                )}

                {snackbarEl}
            </Box>
        );
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ py: 1.5, fontSize: '1rem' }}>
                Partywise Item - {partyName}
            </DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1.5 }}>
                {body}
            </DialogContent>
            <DialogActions sx={{ py: 1, px: 2 }}>
                <Button onClick={onClose} size="small">Cancel</Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    size="small"
                    disabled={gridRows.length === 0 || saving}
                >
                    {saving ? <CircularProgress size={20} /> : 'Save'}
                </Button>
            </DialogActions>

            {snackbarEl}
        </Dialog>
    );
}
